import { hasFeature } from '@/features/feature-flags';
import { FEATURES } from '@/features/feature-flags/feature-flags.constants';
import {
  Mention,
  mentionTrailingSpace,
  removeMention,
  type MentionItem,
  type MentionSource,
} from '@/features/mention';
import { queryClient } from '@/query/client';

import { hivemindGroupsQueryOptions } from '../hivemind.query';
import { type HivemindGroup } from '../hivemind.types';

import { GroupAvatars } from './group-avatars';

import type { Editor } from '@tiptap/core';

const GROUP_KIND = 'group';

const MENU_GROUPS_LIMIT = 5;

export const peopleCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'person' : 'people'}`;

// A user's group names are unique, so the name is the mention's id and what a message sends
export const groupToMention = (group: HivemindGroup): MentionItem => ({
  id: group.name,
  kind: GROUP_KIND,
  name: group.name,
  subtitle: peopleCountLabel(group.members.length),
  isSubtitleSearchable: false,
});

export const insertGroupMention = (editor: Editor, group: HivemindGroup) =>
  editor
    .chain()
    .insertContentAt(editor.state.selection.from, [
      { type: Mention.name, attrs: groupToMention(group) },
      mentionTrailingSpace,
    ])
    .run();

export const removeGroupMention = (editor: Editor, groupName: string) =>
  removeMention(editor, { kind: GROUP_KIND, id: groupName });

export const mentionedGroupNames = (
  mentions: readonly MentionItem[]
): string[] =>
  mentions.flatMap(({ kind, id }) => (kind === GROUP_KIND ? [id] : []));

const listGroups = async (): Promise<HivemindGroup[]> =>
  hasFeature(FEATURES.HIVE) && hasFeature(FEATURES.PEOPLE_WIKI)
    ? queryClient.fetchQuery(hivemindGroupsQueryOptions())
    : [];

export const createGroupSource = (): MentionSource => ({
  kind: GROUP_KIND,
  title: 'Groups',
  maxItems: MENU_GROUPS_LIMIT,
  listItems: async () => (await listGroups()).map(groupToMention),
  renderIcon: (mention, surface) => (
    <GroupAvatars
      groupName={mention.id}
      sizeClassName={surface === 'chip' ? 'size-4' : 'size-5'}
    />
  ),
});
